// Shared formatters for the portal.

const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: '€',
}

/**
 * Round a currency amount to 2 decimal places (half-up via Math.round).
 */
export function roundCurrencyAmount(value: number): number {
  return Math.round(value * 100) / 100
}

/**
 * Format a numeric amount for display inside currency inputs (fr-FR):
 * space as thousands separator, comma as decimal, always 2 fraction digits.
 *
 * `123.45` → `"123,45"`, `1234.56` → `"1 234,56"`
 */
export function formatCurrencyInputValue(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(roundCurrencyAmount(value))
    .replace(/\s/g, ' ')
}

/**
 * Parse a user-typed currency amount that may use `.` or `,` as the decimal
 * separator (FR/EN). Spaces are treated as thousand separators.
 * Result is always rounded to 2 decimal places.
 */
export function parseCurrencyInput(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '')
  if (!trimmed) return null

  const lastDot = trimmed.lastIndexOf('.')
  const lastComma = trimmed.lastIndexOf(',')

  let normalized: string
  if (lastDot === -1 && lastComma === -1) {
    normalized = trimmed
  } else if (lastComma > lastDot) {
    // Comma is the decimal separator; dots (if any) are thousands.
    normalized = trimmed.replace(/\./g, '').replace(',', '.')
  } else {
    // Dot is the decimal separator; commas (if any) are thousands.
    normalized = trimmed.replace(/,/g, '')
  }

  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null

  const n = Number(normalized)
  if (!Number.isFinite(n)) return null
  return roundCurrencyAmount(n)
}

export function isValidCurrencyInput(raw: string): boolean {
  return parseCurrencyInput(raw) !== null
}

/**
 * Allow digits, spaces (thousand separators), `.` / `,`, and optional leading `-`
 * while typing.
 * Returns null when the keystroke / paste should be rejected.
 */
export function sanitizeCurrencyDraft(
  raw: string,
  allowNegative = false
): string | null {
  if (raw === '') return ''

  // Normalize all Unicode spaces to a regular space (fr-FR thousand sep).
  let s = raw.replace(/\s/g, ' ')
  let negative = false
  if (allowNegative && s.trimStart().startsWith('-')) {
    negative = true
    s = s.trimStart().slice(1)
  } else if (/^[+-]/.test(s.trimStart())) {
    return null
  }

  if (/[^\d., ]/.test(s)) return null

  const compact = s.replace(/ /g, '')
  // Separator-only drafts: allow a single `,` or `.` while typing; reject
  // mixes like `,.` / `.,` / `,,` / `..`.
  if (compact !== '' && !/\d/.test(compact)) {
    if (compact !== ',' && compact !== '.') return null
  }
  if (!isValidCurrencySeparatorLayout(compact)) return null

  return negative ? `-${s.trimStart()}` : s
}

/**
 * Valid layouts:
 * - one decimal sep only: `123,45` / `123.45` / `123,`
 * - FR thousands + decimal: `1.234.567,89` (dots before a single trailing comma)
 * - US thousands + decimal: `1,234,567.89` (commas before a single trailing dot)
 */
function isValidCurrencySeparatorLayout(compact: string): boolean {
  if (!compact) return true

  const lastComma = compact.lastIndexOf(',')
  const lastDot = compact.lastIndexOf('.')
  const commaCount = (compact.match(/,/g) ?? []).length
  const dotCount = (compact.match(/\./g) ?? []).length

  if (lastComma === -1 && lastDot === -1) return true

  if (lastComma > lastDot) {
    // Comma is the decimal separator: at most one comma; dots only before it.
    if (commaCount > 1) return false
    const fraction = compact.slice(lastComma + 1)
    return !fraction.includes('.') && !fraction.includes(',')
  }

  // Dot is the decimal separator: at most one dot; commas only before it.
  if (dotCount > 1) return false
  const fraction = compact.slice(lastDot + 1)
  return !fraction.includes('.') && !fraction.includes(',')
}

export function formatCurrency(
  value: number | null | undefined,
  currency = 'EUR',
  fractionDigits = 0,
  display: 'prefix' | 'suffix' = 'prefix'
): string {
  const n = value ?? 0
  void display
  const amount = formatLocalizedNumber(n, fractionDigits)
  const symbol = CURRENCY_SYMBOL[currency] ?? currency
  return `${amount} ${symbol}`
}

function formatLocalizedNumber(value: number, fractionDigits: number): string {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
    .format(value)
    .replace(/\s/g, ' ')
}

export function formatInteger(value: number | null | undefined): string {
  return formatLocalizedNumber(value ?? 0, 0)
}

export function formatWelcomeDate(locale: string): string {
  return new Intl.DateTimeFormat(locale.startsWith('fr') ? 'fr-FR' : 'en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date())
}

// dd/mm/yyyy per AC-2.2.4-013 / 2.2.5-014.
export function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`
}

// Inclusive whole months covered by [start, end] (contract periods are inclusive).
// A 1-year contract 01/09/2026–31/08/2027 yields 12. Negative when end precedes start.
export function monthsBetween(start: Date, end: Date): number {
  const exclusiveEnd = new Date(end)
  exclusiveEnd.setDate(exclusiveEnd.getDate() + 1)
  let months =
    (exclusiveEnd.getFullYear() - start.getFullYear()) * 12 +
    (exclusiveEnd.getMonth() - start.getMonth())
  if (exclusiveEnd.getDate() < start.getDate()) months -= 1
  return months
}

export function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diff = d.getTime() - today.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return `${Math.round(value)}%`
}

export function formatPostalAddress(parts: {
  address?: string | null
  postal_code?: string | null
  city?: string | null
  country?: string | null
}): string {
  return [parts.address, parts.postal_code, parts.city, parts.country]
    .filter(Boolean)
    .join(', ')
}
